import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  type ResumeData,
  type ResumeContent,
  type ResumeConsent,
  type ResumeSettings,
  type ResumeSource,
  type ResumeShare,
  DEFAULT_RESUME_CONTENT,
  DEFAULT_RESUME_SETTINGS,
} from '@/lib/resume/types';
import { normalizeResumeContent } from '@/lib/resume/normalize-content';

// Simple debounce utility
function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

function generateEntityId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

function ensureUniqueIds<T extends { id?: string | null }>(items?: T[] | null): Array<T & { id: string }> {
  const seen = new Set<string>();
  return (items ?? []).map((item) => {
    let id = typeof item.id === 'string' ? item.id.trim() : '';
    if (!id) {
      id = generateEntityId();
    }
    while (seen.has(id)) {
      id = generateEntityId();
    }
    seen.add(id);
    return { ...item, id } as T & { id: string };
  });
}

function normalizeResumeContentIds(content: ResumeContent): ResumeContent {
  const normalizedContent = normalizeResumeContent(content);
  const normalizedSkills = normalizedContent.skills ?? DEFAULT_RESUME_CONTENT.skills;

  return {
    ...normalizedContent,
    links: ensureUniqueIds(normalizedContent.links),
    employment: ensureUniqueIds(normalizedContent.employment),
    education: ensureUniqueIds(normalizedContent.education),
    certifications: ensureUniqueIds(normalizedContent.certifications),
    customSections: ensureUniqueIds(normalizedContent.customSections),
    skills: {
      ...normalizedSkills,
      categories: ensureUniqueIds(normalizedSkills.categories),
    },
  };
}

interface ResumeBuilderState {
  // Current resume data
  resume: ResumeData | null;
  isLoading: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
  error: string | null;
  /**
   * Anonymous (guest) resume access token, once Share & Export has minted
   * one — see `app/api/resume-builder/anonymous/route.ts`. Null for
   * authenticated resumes and for a guest resume that has never been shared.
   * While set, `saveResume()` syncs a guest's edits to that anonymous
   * server-side resume instead of only marking them saved locally.
   */
  guestSyncToken: string | null;

  /**
   * Hover-preview overrides for the Style selector. Set while the pointer is
   * over a template/color option so the preview can show it live; null falls
   * back to the saved `resume.settings` value. Deliberately kept OUT of
   * `resume.settings` and never marked as an unsaved change — this is a
   * transient view-only override, not an edit, so it must never reach
   * autosave or the server.
   */
  previewTemplateId: string | null;
  previewColorSchemeId: string | null;

  // Actions
  setResume: (resume: ResumeData | null) => void;
  updateContent: (content: Partial<ResumeContent>) => void;
  updateSettings: (settings: Partial<ResumeSettings>) => void;
  setPreviewStyle: (templateId: string | null, colorSchemeId: string | null) => void;
  clearPreviewStyle: () => void;
  updateName: (name: string) => void;
  updateShare: (share: ResumeShare) => void;
  setLoading: (isLoading: boolean) => void;
  setSaving: (isSaving: boolean) => void;
  setError: (error: string | null) => void;
  setGuestSyncToken: (token: string | null) => void;
  markSaved: () => void;

  // Array field helpers
  addLink: (link: ResumeContent['links'][0]) => void;
  updateLink: (id: string, link: Partial<ResumeContent['links'][0]>) => void;
  removeLink: (id: string) => void;

  addEmployment: (employment: ResumeContent['employment'][0]) => void;
  updateEmployment: (id: string, employment: Partial<ResumeContent['employment'][0]>) => void;
  removeEmployment: (id: string) => void;

  addEducation: (education: ResumeContent['education'][0]) => void;
  updateEducation: (id: string, education: Partial<ResumeContent['education'][0]>) => void;
  removeEducation: (id: string) => void;

  addCertification: (certification: ResumeContent['certifications'][0]) => void;
  updateCertification: (id: string, certification: Partial<ResumeContent['certifications'][0]>) => void;
  removeCertification: (id: string) => void;

  addCustomSection: (section: ResumeContent['customSections'][0]) => void;
  updateCustomSection: (id: string, section: Partial<ResumeContent['customSections'][0]>) => void;
  removeCustomSection: (id: string) => void;
  reorderCustomSections: (orderedIds: string[]) => void;

  updateConsent: (consent: Partial<ResumeConsent>) => void;

  addSkillCategory: (category: ResumeContent['skills']['categories'][0]) => void;
  updateSkillCategory: (id: string, category: Partial<ResumeContent['skills']['categories'][0]>) => void;
  removeSkillCategory: (id: string) => void;

  // API methods
  loadResume: (id: string) => Promise<void>;
  saveResume: () => Promise<void>;
  createVersion: () => Promise<ResumeData | null>;

  // Autosave
  _autosave: () => void;
}

export const useResumeBuilderStore = create<ResumeBuilderState>()(
  subscribeWithSelector((set, get) => {
    // Debounced autosave function
    const debouncedSave = debounce(async () => {
      const state = get();
      if (state.resume && state.hasUnsavedChanges && !state.isSaving) {
        await state.saveResume();
      }
    }, 2000);

    return {
      resume: null,
      isLoading: false,
      isSaving: false,
      lastSaved: null,
      hasUnsavedChanges: false,
      error: null,
      guestSyncToken: null,
      previewTemplateId: null,
      previewColorSchemeId: null,

      setResume: (resume) => {
        set({
          resume: resume
            ? {
                ...resume,
                content: normalizeResumeContentIds(resume.content ?? DEFAULT_RESUME_CONTENT),
              }
            : null,
          hasUnsavedChanges: false,
          error: null,
        });
      },

      updateContent: (content) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                ...content,
                personalInfo: content.personalInfo
                  ? { ...state.resume.content.personalInfo, ...content.personalInfo }
                  : state.resume.content.personalInfo,
                profile: content.profile
                  ? { ...state.resume.content.profile, ...content.profile }
                  : state.resume.content.profile,
                contact: content.contact
                  ? { ...state.resume.content.contact, ...content.contact }
                  : state.resume.content.contact,
                skills: content.skills
                  ? { ...state.resume.content.skills, ...content.skills }
                  : state.resume.content.skills,
                consent: content.consent
                  ? { ...state.resume.content.consent, ...content.consent }
                  : state.resume.content.consent,
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      updateSettings: (settings) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              settings: { ...state.resume.settings, ...settings },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      setPreviewStyle: (templateId, colorSchemeId) => {
        set({ previewTemplateId: templateId, previewColorSchemeId: colorSchemeId });
      },

      clearPreviewStyle: () => {
        set({ previewTemplateId: null, previewColorSchemeId: null });
      },

      updateName: (name) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: { ...state.resume, name },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      updateShare: (share) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              share,
            },
          };
        });
      },

      setLoading: (isLoading) => set({ isLoading }),
      setSaving: (isSaving) => set({ isSaving }),
      setError: (error) => set({ error }),
      setGuestSyncToken: (token) => set({ guestSyncToken: token }),

      markSaved: () => {
        set({ hasUnsavedChanges: false, lastSaved: new Date() });
      },

      // Link helpers
      addLink: (link) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                links: [...state.resume.content.links, link],
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      updateLink: (id, link) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                links: state.resume.content.links.map((l) => (l.id === id ? { ...l, ...link } : l)),
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      removeLink: (id) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                links: state.resume.content.links.filter((l) => l.id !== id),
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      // Employment helpers
      addEmployment: (employment) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                employment: [...state.resume.content.employment, employment],
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      updateEmployment: (id, employment) => {
        set((state) => {
          if (!state.resume) return state;
          let hasUpdated = false;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                employment: state.resume.content.employment.map((e) => {
                  if (!hasUpdated && e.id === id) {
                    hasUpdated = true;
                    return { ...e, ...employment };
                  }
                  return e;
                }),
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      removeEmployment: (id) => {
        set((state) => {
          if (!state.resume) return state;
          let hasRemoved = false;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                employment: state.resume.content.employment.filter((e) => {
                  if (!hasRemoved && e.id === id) {
                    hasRemoved = true;
                    return false;
                  }
                  return true;
                }),
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      // Education helpers
      addEducation: (education) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                education: [...state.resume.content.education, education],
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      updateEducation: (id, education) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                education: state.resume.content.education.map((e) =>
                  e.id === id ? { ...e, ...education } : e
                ),
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      removeEducation: (id) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                education: state.resume.content.education.filter((e) => e.id !== id),
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      // Certification helpers
      addCertification: (certification) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                certifications: [...state.resume.content.certifications, certification],
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      updateCertification: (id, certification) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                certifications: state.resume.content.certifications.map((c) =>
                  c.id === id ? { ...c, ...certification } : c
                ),
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      removeCertification: (id) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                certifications: state.resume.content.certifications.filter((c) => c.id !== id),
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      // Custom section helpers
      addCustomSection: (section) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                customSections: [...state.resume.content.customSections, section],
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      updateCustomSection: (id, section) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                customSections: state.resume.content.customSections.map((s) =>
                  s.id === id ? { ...s, ...section } : s
                ),
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      removeCustomSection: (id) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                customSections: state.resume.content.customSections.filter((s) => s.id !== id),
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      reorderCustomSections: (orderedIds) => {
        set((state) => {
          if (!state.resume) return state;
          const sections = state.resume.content.customSections;
          const reordered = orderedIds
            .map((id, index) => {
              const section = sections.find((s) => s.id === id);
              return section ? { ...section, order: index } : null;
            })
            .filter(Boolean) as ResumeContent['customSections'];

          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                customSections: reordered,
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      // Consent clause helper. Merges into the existing value so toggling the
      // checkbox never discards wording the user (or an import) already put
      // there.
      updateConsent: (consent) => {
        set((state) => {
          if (!state.resume) return state;
          const current = state.resume.content.consent ?? { enabled: false, text: '' };
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                consent: { ...current, ...consent },
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      // Skill category helpers
      addSkillCategory: (category) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                skills: {
                  ...state.resume.content.skills,
                  categories: [...state.resume.content.skills.categories, category],
                },
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      updateSkillCategory: (id, category) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                skills: {
                  ...state.resume.content.skills,
                  categories: state.resume.content.skills.categories.map((c) =>
                    c.id === id ? { ...c, ...category } : c
                  ),
                },
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      removeSkillCategory: (id) => {
        set((state) => {
          if (!state.resume) return state;
          return {
            resume: {
              ...state.resume,
              content: {
                ...state.resume.content,
                skills: {
                  ...state.resume.content.skills,
                  categories: state.resume.content.skills.categories.filter((c) => c.id !== id),
                },
              },
            },
            hasUnsavedChanges: true,
          };
        });
        debouncedSave();
      },

      // API methods
      loadResume: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`/api/resume-builder/${id}`);
          if (!response.ok) {
            throw new Error('Failed to load resume');
          }
          const data = await response.json();
          set({
            resume: {
              ...data.resume,
              content: normalizeResumeContentIds(data.resume.content ?? DEFAULT_RESUME_CONTENT),
            },
            isLoading: false,
            hasUnsavedChanges: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load resume',
            isLoading: false,
          });
        }
      },

      saveResume: async () => {
        const state = get();
        if (!state.resume || state.isSaving) return;

        // Guest mode: no account, so there is no `/api/resume-builder/:id`
        // to PATCH. Before Share & Export has minted a server-side
        // anonymous resume (`guestSyncToken` unset), the localStorage sync
        // the component already does via useEffect is the only copy that
        // exists — just mark the data "saved" so the UI doesn't show a
        // stale unsaved-changes indicator. Once a token exists, every edit
        // after that is also synced there, debounced the same way an
        // authenticated save is.
        if (state.resume.id.startsWith('guest-')) {
          const token = state.guestSyncToken;
          if (!token) {
            set({ hasUnsavedChanges: false, lastSaved: new Date() });
            return;
          }

          set({ isSaving: true, error: null });
          try {
            const response = await fetch('/api/resume-builder/anonymous', {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                token,
                name: state.resume.name,
                content: state.resume.content,
                settings: state.resume.settings,
              }),
            });

            if (!response.ok) {
              // Expired or cleaned up server-side — stop retrying against a
              // dead token on every keystroke. The edits stay safe in
              // localStorage regardless; the next Share & Export click will
              // notice the token is gone and mint a fresh one.
              if (response.status === 404) {
                set({ guestSyncToken: null });
              }
              throw new Error('Failed to save resume');
            }

            set({ isSaving: false, hasUnsavedChanges: false, lastSaved: new Date() });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : 'Failed to save resume',
              isSaving: false,
            });
          }
          return;
        }

        set({ isSaving: true, error: null });
        try {
          const response = await fetch(`/api/resume-builder/${state.resume.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: state.resume.name,
              content: state.resume.content,
              settings: state.resume.settings,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to save resume');
          }

          set({
            isSaving: false,
            hasUnsavedChanges: false,
            lastSaved: new Date(),
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to save resume',
            isSaving: false,
          });
        }
      },

      createVersion: async () => {
        const state = get();
        if (!state.resume) return null;

        set({ isSaving: true, error: null });
        try {
          // Save current changes first
          if (state.hasUnsavedChanges) {
            await state.saveResume();
          }

          const response = await fetch(`/api/resume-builder/${state.resume.id}/version`, {
            method: 'POST',
          });

          if (!response.ok) {
            throw new Error('Failed to create version');
          }

          const data = await response.json();
          set({
            resume: {
              ...data.resume,
              content: normalizeResumeContentIds(data.resume.content ?? DEFAULT_RESUME_CONTENT),
            },
            isSaving: false,
            hasUnsavedChanges: false,
            lastSaved: new Date(),
          });

          return data.resume;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create version',
            isSaving: false,
          });
          return null;
        }
      },

      _autosave: debouncedSave,
    };
  })
);
